// Support-chat provider scripts (Pylon, Intercom) load asynchronously: their
// inline loader can set the provider global after a caller has already
// subscribed. A subscription made before the global exists is a no-op that
// never retries, so that session silently misses every event the provider would
// emit (open/close, unread count) — which can under-count metrics derived from
// those events. `subscribeWhenReady` defers attachment until the provider is
// ready, polling for it, then runs `attach` (which performs the real
// registration and returns its own cleanup).
//
// The returned cleanup either cancels the pending poll (when the provider never
// became ready) or runs `attach`'s cleanup (once handlers were attached), so no
// timer or handler outlives the caller. Polling stops after `timeoutMs` so a
// provider that never loads cannot leak a perpetual timer.
const DEFAULT_POLL_INTERVAL_MS = 200
const DEFAULT_TIMEOUT_MS = 15_000

export function subscribeWhenReady(
  isReady: () => boolean,
  attach: () => () => void,
  {
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  }: { pollIntervalMs?: number; timeoutMs?: number } = {},
): () => void {
  if (isReady()) {
    return attach()
  }

  let cleanup: (() => void) | undefined
  let timeoutId: number | undefined
  let cancelled = false
  const startedAt = Date.now()

  const poll = (): void => {
    if (cancelled) {
      return
    }
    if (isReady()) {
      cleanup = attach()
      return
    }
    if (Date.now() - startedAt >= timeoutMs) {
      return
    }
    timeoutId = window.setTimeout(poll, pollIntervalMs)
  }

  timeoutId = window.setTimeout(poll, pollIntervalMs)

  return () => {
    cancelled = true
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
    }
    cleanup?.()
  }
}
